// MatchScheduleSlates — builds full-screen slates of green MatchTiles.
//   mode="pool"  (Round Robin): one slate per pool, split into pages of
//                PER_SLATE when a pool is too large. Final gets its own slate.
//   mode="flat"  (Tournament): existing schedule order (time → court → round/
//                match number), chunked into slates. Single slate = no rotation.
import React, { useMemo } from 'react';
import SlateRotator from './SlateRotator';
import MatchTile from './MatchTile';

const PER_SLATE = 8; // 4 across × 2 rows — readable at 1366x768 and 1920x1080
const COLS = 4;

function sortMatches(matches) {
  const key = (m) => [
    m.scheduled_at ? new Date(m.scheduled_at).getTime() : Number.MAX_SAFE_INTEGER,
    (m.court_label || '').toLowerCase(),
    m.round_number || 0,
    m.match_number || 0,
  ];
  return [...matches].sort((a, b) => {
    const ka = key(a); const kb = key(b);
    for (let i = 0; i < ka.length; i += 1) {
      if (ka[i] < kb[i]) return -1;
      if (ka[i] > kb[i]) return 1;
    }
    return 0;
  });
}

function chunk(arr, n) {
  const out = [];
  for (let i = 0; i < arr.length; i += n) out.push(arr.slice(i, i + n));
  return out;
}

function buildPoolSlates(matches, poolLabels, championshipFormat, poolChampionships) {
  const byPool = {};
  const finals = [];
  matches.forEach((m) => {
    if (m.is_final) { finals.push(m); return; }
    (byPool[m.pool_number] = byPool[m.pool_number] || []).push(m);
  });
  const slates = [];
  Object.keys(byPool).sort((a, b) => Number(a) - Number(b)).forEach((p) => {
    const label = poolLabels[String(p)] || `Pool ${p}`;
    const pages = chunk(sortMatches(byPool[p]), PER_SLATE);
    pages.forEach((items, i) => slates.push({
      key: `pool-${p}-${i}`,
      title: label,
      subtitle: pages.length > 1 ? `Page ${i + 1} of ${pages.length}` : null,
      items,
    }));
  });
  if (championshipFormat === 'per_pool') {
    // ONE combined "Championships" slate: all pool finals as larger cards,
    // each with its own division title in the card header.
    const champs = poolChampionships || [];
    const titleFor = (m) => {
      const pc = champs.find((x) => x.final_match_id === m.id)
        || champs.find((x) => x.pool_number === m.pool_number);
      return pc ? pc.title : `${poolLabels[String(m.pool_number)] || `Pool ${m.pool_number}`} Championship`;
    };
    const champFinals = finals
      .filter((m) => m.entry1 || m.entry2 || m.scheduled_at || m.court_label)
      .sort((a, b) => (a.pool_number || 0) - (b.pool_number || 0))
      .map((m) => ({ ...m, __champTitle: titleFor(m) }));
    if (champFinals.length) {
      slates.push({ key: 'championships', title: 'Championships', subtitle: null, championship: true, items: champFinals });
    }
  } else if (championshipFormat !== 'none') {
    const shown = finals.filter((m) => m.entry1 || m.entry2);
    if (shown.length) slates.push({ key: 'final', title: 'Final', subtitle: null, items: sortMatches(shown) });
  }
  return slates;
}

function buildFlatSlates(matches) {
  const playable = matches.filter((m) => m.score_text !== '(bye)' && (m.entry1 || m.entry2 || m.scheduled_at || m.court_label));
  const pages = chunk(sortMatches(playable), PER_SLATE);
  return pages.map((items, i) => ({
    key: `page-${i}`,
    title: 'Match Schedule',
    subtitle: pages.length > 1 ? `Page ${i + 1} of ${pages.length}` : null,
    items,
  }));
}

const TILE_MAX_H = 360; // cards stay compact on tall screens; grid centers vertically

function SlateGrid({ items }) {
  const rows = Math.max(1, Math.ceil(PER_SLATE / COLS));
  const gap = 'clamp(10px, 1.1vw, 20px)';
  return (
    <div className="h-full w-full flex flex-col justify-center min-h-0">
      <div
        className="w-full grid min-h-0"
        style={{
          height: '100%',
          maxHeight: `calc(${rows} * ${TILE_MAX_H}px + ${rows - 1} * ${gap})`,
          gridTemplateColumns: `repeat(${COLS}, minmax(0, 1fr))`,
          gridTemplateRows: `repeat(${rows}, minmax(0, 1fr))`,
          gap,
        }}
      >
        {items.map((m) => <MatchTile key={m.id} match={m} />)}
      </div>
    </div>
  );
}

// Championship slate — all pool finals on ONE screen, one larger card each
// with its division title in the header. Single row so all 3 stay visible
// with no scrolling on TV / iPad landscape.
function ChampionshipGrid({ items }) {
  const cols = Math.min(items.length, 4) || 1;
  const gap = 'clamp(12px, 1.4vw, 26px)';
  return (
    <div className="h-full w-full flex flex-col justify-center min-h-0">
      <div
        className="w-full grid min-h-0 mx-auto"
        style={{
          height: '100%',
          maxHeight: 'min(100%, clamp(360px, 66vh, 640px))',
          gridTemplateColumns: `repeat(${cols}, minmax(0, 1fr))`,
          gap,
        }}
      >
        {items.map((m) => <MatchTile key={m.id} match={m} title={m.__champTitle} />)}
      </div>
    </div>
  );
}

export default function MatchScheduleSlates({ matches, mode = 'flat', poolLabels = {}, championshipFormat, poolChampionships = [], active = true, testidPrefix = 'view-schedule' }) {
  const slates = useMemo(
    () => (mode === 'pool' ? buildPoolSlates(matches || [], poolLabels, championshipFormat, poolChampionships) : buildFlatSlates(matches || [])),
    [matches, mode, poolLabels, championshipFormat, poolChampionships],
  );

  if (slates.length === 0) {
    return (
      <div data-testid={`${testidPrefix}-empty`} className="h-full flex items-center justify-center text-[#6a766e]" style={{ fontSize: 'clamp(14px, 1.2vw, 18px)' }}>
        No matches to display yet.
      </div>
    );
  }

  return (
    <SlateRotator
      slates={slates}
      active={active}
      testidPrefix={testidPrefix}
      renderSlate={(s) => (s.championship ? <ChampionshipGrid items={s.items} /> : <SlateGrid items={s.items} />)}
    />
  );
}
